import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from 'next-themes';

import { AppScreen, QuizState, Difficulty, LessonContent } from '@/types';
import { STATIC_QUIZ_DATA } from '@/data/quizData';
import { useAIQuiz, useAILesson } from '@/hooks/useAIContent';
import { useAuth } from '@/hooks/useAuth';
import { useCompletedLessons, useQuizAttempts } from '@/hooks/useProgress';

import AuthScreen from '@/components/auth/AuthScreen';
import LoadingSpinner from '@/components/common/LoadingSpinner';

const Dashboard = lazy(() => import('@/components/dashboard/Dashboard'));
const LessonHub = lazy(() => import('@/components/lessons/LessonHub'));
const LessonViewer = lazy(() => import('@/components/lessons/LessonViewer'));
const TopicSelection = lazy(() => import('@/components/quiz/QuizSetup').then(m => ({ default: m.TopicSelection })));
const CountSelection = lazy(() => import('@/components/quiz/QuizSetup').then(m => ({ default: m.CountSelection })));
const QuizCard = lazy(() => import('@/components/quiz/QuizCard'));
const QuizResults = lazy(() => import('@/components/quiz/QuizResults'));
const AINewsHub = lazy(() => import('@/components/news/AINewsHub'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));

const queryClient = new QueryClient();

const AppContent = () => {
  const [screen, setScreen] = useState<AppScreen>(AppScreen.AUTH);
  const [currentLessonDifficulty, setCurrentLessonDifficulty] = useState<Difficulty>('beginner');
  const [activeLesson, setActiveLesson] = useState<{
    content: LessonContent | null;
    loading: boolean;
    loadingStep: string;
  }>({ content: null, loading: false, loadingStep: '' });

  const [quiz, setQuiz] = useState<QuizState>({
    difficulty: 'beginner',
    questionCount: 5,
    questions: [],
    currentIndex: 0,
    score: 0,
    selectedAnswer: null,
    userAnswers: [],
    isCompleted: false,
    isLoading: false,
  });

  // Auth hook
  const { user, profile, isLoading: authLoading, isAuthenticated, signUp, signIn, signOut } = useAuth();

  // Progress hooks (only active when authenticated)
  const { completedLessons, markLessonComplete } = useCompletedLessons(user?.id || null);
  const { saveQuizAttempt, getStats } = useQuizAttempts(user?.id || null);

  // AI hooks
  const { generateQuiz, isLoading: quizLoading } = useAIQuiz();
  const { generateLesson, isLoading: lessonLoading, loadingStep } = useAILesson();

  // Handle auth state changes
  useEffect(() => {
    if (!authLoading) {
      if (isAuthenticated) {
        setScreen(AppScreen.START);
      } else {
        setScreen(AppScreen.AUTH);
      }
    }
  }, [authLoading, isAuthenticated]);

  const handleLogout = async () => {
    await signOut();
    setScreen(AppScreen.AUTH);
  };

  const handleSelectDifficulty = (difficulty: Difficulty) => {
    setQuiz((prev) => ({ ...prev, difficulty }));
  };

  const loadLesson = useCallback(async (topic: string, skipCache?: boolean) => {
    setActiveLesson({ content: null, loading: true, loadingStep: skipCache ? 'Regenerating fresh content...' : 'Analyzing topic...' });
    
    const lesson = await generateLesson(topic, currentLessonDifficulty, skipCache);
    
    if (lesson) {
      setActiveLesson({
        content: lesson,
        loading: false,
        loadingStep: '',
      });
      setScreen(AppScreen.LESSON_VIEW);
    } else {
      setActiveLesson({ content: null, loading: false, loadingStep: '' });
    }
  }, [generateLesson, currentLessonDifficulty]);

  const handleMarkLessonComplete = async (topic: string) => {
    await markLessonComplete(topic, currentLessonDifficulty);
  };

  const initQuiz = useCallback(async (count: number, topic?: string) => {
    setQuiz((prev) => ({ ...prev, isLoading: true, questionCount: count, userTopic: topic }));
    setScreen(AppScreen.QUIZ);

    const questions = await generateQuiz(quiz.difficulty, count, topic);
    
    if (questions.length > 0) {
      setQuiz((prev) => ({
        ...prev,
        questions,
        isLoading: false,
        currentIndex: 0,
        score: 0,
        selectedAnswer: null,
        userAnswers: new Array(questions.length).fill(null),
        isCompleted: false,
      }));
    } else {
      console.log('Falling back to static quiz data');
      const baseData = STATIC_QUIZ_DATA[quiz.difficulty] || STATIC_QUIZ_DATA.beginner;
      const fallbackQuestions = [...baseData].sort(() => Math.random() - 0.5).slice(0, count);

      setQuiz((prev) => ({
        ...prev,
        questions: fallbackQuestions,
        isLoading: false,
        currentIndex: 0,
        score: 0,
        selectedAnswer: null,
        userAnswers: new Array(fallbackQuestions.length).fill(null),
        isCompleted: false,
      }));
    }
  }, [generateQuiz, quiz.difficulty]);

  const handleAnswerSelect = (index: number) => {
    if (quiz.selectedAnswer !== null) return;

    const isCorrect = index === quiz.questions[quiz.currentIndex].correct;
    const newAnswers = [...quiz.userAnswers];
    newAnswers[quiz.currentIndex] = index;

    setQuiz((prev) => ({
      ...prev,
      selectedAnswer: index,
      userAnswers: newAnswers,
      score: isCorrect ? prev.score + 1 : prev.score,
    }));
  };

  const handleNextQuestion = async () => {
    if (quiz.currentIndex + 1 >= quiz.questions.length) {
      setQuiz((prev) => ({ ...prev, isCompleted: true }));
      
      // Save quiz attempt to database
      await saveQuizAttempt(
        quiz.difficulty,
        quiz.questions.length,
        quiz.score,
        quiz.userTopic
      );
      
      setScreen(AppScreen.RESULTS);
    } else {
      setQuiz((prev) => ({
        ...prev,
        currentIndex: prev.currentIndex + 1,
        selectedAnswer: null,
      }));
    }
  };

  const resetQuiz = () => {
    setQuiz({
      difficulty: 'beginner',
      questionCount: 5,
      questions: [],
      currentIndex: 0,
      score: 0,
      selectedAnswer: null,
      userAnswers: [],
      isCompleted: false,
      isLoading: false,
    });
    setScreen(AppScreen.START);
    setActiveLesson({ content: null, loading: false, loadingStep: '' });
  };

  // Show loading while checking auth
  if (authLoading) {
    return <LoadingSpinner message="Loading..." subMessage="Checking authentication" />;
  }

  // Create a user object for Dashboard (adapting from Supabase user)
  const dashboardUser = profile ? {
    id: user!.id,
    name: profile.display_name || user!.email?.split('@')[0] || 'User',
    email: user!.email || '',
    role: 'user' as const,
    joinedAt: profile.created_at,
  } : null;

  // Render based on screen
  const renderScreen = () => {
    switch (screen) {
      case AppScreen.AUTH:
        return <AuthScreen onSignUp={signUp} onSignIn={signIn} />;

      case AppScreen.START:
        return dashboardUser ? (
          <Dashboard
            user={dashboardUser}
            onNavigate={setScreen}
            onSelectDifficulty={handleSelectDifficulty}
            onLogout={handleLogout}
          />
        ) : null;

      case AppScreen.LESSONS:
        return (activeLesson.loading || lessonLoading) ? (
          <LoadingSpinner message={loadingStep || activeLesson.loadingStep} subMessage="Powered by Gemini 3 Pro" showGenerationNote />
        ) : (
          <LessonHub
            completedLessons={completedLessons}
            onBack={() => setScreen(AppScreen.START)}
            onSelectLesson={loadLesson}
          />
        );

      case AppScreen.LESSON_VIEW:
        return (activeLesson.loading || lessonLoading) ? (
          <LoadingSpinner message={loadingStep || activeLesson.loadingStep} subMessage="Powered by Gemini 3 Pro" showGenerationNote />
        ) : activeLesson.content ? (
          <LessonViewer
            content={activeLesson.content}
            currentDifficulty={currentLessonDifficulty}
            isCompleted={completedLessons.includes(activeLesson.content.title)}
            onBack={() => {
              setActiveLesson({ content: null, loading: false, loadingStep: '' });
              setScreen(AppScreen.LESSONS);
            }}
            onStartQuiz={() => {
              handleMarkLessonComplete(activeLesson.content!.title);
              handleSelectDifficulty(currentLessonDifficulty);
              setScreen(AppScreen.COUNT_SELECTION);
            }}
            onMarkComplete={() => handleMarkLessonComplete(activeLesson.content!.title)}
            onDifficultyChange={(d) => {
              setCurrentLessonDifficulty(d);
              loadLesson(activeLesson.content!.title);
            }}
            onRegenerate={() => loadLesson(activeLesson.content!.title, true)}
          />
        ) : null;

      case AppScreen.TOPIC_SELECTION:
        return (
          <TopicSelection
            onBack={() => setScreen(AppScreen.START)}
            onSubmit={(topic) => initQuiz(5, topic)}
          />
        );

      case AppScreen.NEWS:
        return (
          <AINewsHub onBack={() => setScreen(AppScreen.START)} />
        );

      case AppScreen.COUNT_SELECTION:
        return (
          <CountSelection
            difficulty={quiz.difficulty}
            onBack={() => setScreen(AppScreen.START)}
            onSubmit={(count) => initQuiz(count)}
          />
        );

      case AppScreen.QUIZ:
        return (quiz.isLoading || quizLoading) ? (
          <LoadingSpinner message="Generating Quiz..." subMessage="Powered by Gemini 3 Pro" showGenerationNote />
        ) : (
          <QuizCard
            question={quiz.questions[quiz.currentIndex]}
            index={quiz.currentIndex}
            total={quiz.questions.length}
            selectedAnswer={quiz.selectedAnswer}
            onSelect={handleAnswerSelect}
            onNext={handleNextQuestion}
            onBack={resetQuiz}
          />
        );

      case AppScreen.RESULTS:
        return (
          <QuizResults
            quiz={quiz}
            onRestart={() => {
              setQuiz((prev) => ({
                ...prev,
                currentIndex: 0,
                score: 0,
                selectedAnswer: null,
                userAnswers: [],
                isCompleted: false,
              }));
              setScreen(AppScreen.QUIZ);
              initQuiz(quiz.questionCount, quiz.userTopic);
            }}
            onHome={resetQuiz}
          />
        );

      default:
        return null;
    }
  };

  return <main className="min-h-screen bg-background"><Suspense fallback={<LoadingSpinner message="Loading..." />}>{renderScreen()}</Suspense></main>;
};

const App = () => {
  const path = window.location.pathname;
  const staticPage = path === '/terms' ? <TermsPage /> : path === '/privacy' ? <PrivacyPage /> : path === '/contact' ? <ContactPage /> : null;
  if (staticPage) return <Suspense fallback={<LoadingSpinner message="Loading..." />}>{staticPage}</Suspense>;
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" storageKey="ot-theme">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <AppContent />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

export default App;
